import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import { FilterFn } from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { Route } from "next";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function resolveRedirectPath(path: string, fallback: string): string {
    return path.startsWith("/") && !path.startsWith("//") ? path : fallback;
}

export const protocol =
    process.env.NODE_ENV === "production" ? "https" : "http";
// Bare root host from env — never includes a port. Mirrors server-utils.ts.
const rootHost = process.env.NEXT_PUBLIC_BETTER_AUTH_DOMAIN || "localhost";

// Everywhere but production the app runs on :3000, so the port is part of the host.
export const rootDomain =
    process.env.NODE_ENV === "production" ? rootHost : `${rootHost}:3000`;

export const rootDomainWithProtocolClient = `${protocol}://${rootDomain}`;

export const constructUrlClient = (pathName: string, subDomain: string) => {
    return `${protocol}://${subDomain}.${rootDomain}${pathName}` as Route;
};
export const normalizeName = (
    name: string,
    type: "first" | "last" | "full",
) => {
    const [firstName, lastName] = name.split(" ");

    switch (type) {
        case "first":
            return firstName;
        case "last":
            return lastName;
        case "full":
            return `${firstName} ${lastName}`;
    }
};
export const capitalize = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
};

export function formatDate(date: Date | string) {
    return format(date, "dd-MMM-yyyy");
}

export function formatTime(date: Date | string) {
    return format(date, "hh:mm a");
}

export function formatDuration(start: Date | string, end: Date | string) {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffInMinutes = Math.round(
        (endDate.getTime() - startDate.getTime()) / (1000 * 60),
    );

    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;

    if (hours === 0) {
        return `${minutes}m`;
    }

    if (minutes === 0) {
        return `${hours}h`;
    }

    return `${hours}h ${minutes}m`;
}

export const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(value);
};

// does this 12,50,00
export const formatNumber = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
        style: "decimal",
        maximumFractionDigits: 0,
    }).format(value);
};

// does this 12L
export const formatCompactNumberCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
        notation: "compact",
        compactDisplay: "short",
    }).format(value);
};
export function formatMinutes(totalMinutes: number) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;

    return `${hours}h ${minutes}m`;
}
export const getInitials = (name: string) => {
    if (!name) return "";
    return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
};
export const fetcher = async <T = unknown>(url: string): Promise<T> => {
    const res = await fetch(url);

    const text = await res.text();
    let parsed: unknown = null;
    try {
        parsed = text ? JSON.parse(text) : null;
    } catch {
        throw new Error(
            res.ok ? "Unexpected server response" : "Something went wrong",
        );
    }

    if (!res.ok) {
        throw new Error(
            (parsed as { error?: string })?.error || "Something went wrong",
        );
    }

    return parsed as T;
};

export const fuzzyFilter = <TData>(
    row: Parameters<FilterFn<TData>>[0],
    columnId: Parameters<FilterFn<TData>>[1],
    value: Parameters<FilterFn<TData>>[2],
    addMeta: Parameters<FilterFn<TData>>[3],
) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta?.({ itemRank });

    return itemRank.passed;
};

export function formatMinutesToDuration(totalMinutes: number): string {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${String(m).padStart(2, "0")}m`;
}

export function parseUserAgent(ua: string): { browser: string; os: string } {
    let browser = "Unknown browser";
    let os = "Unknown OS";

    if (/Edg\//.test(ua)) browser = "Edge";
    else if (/OPR\/|Opera/.test(ua)) browser = "Opera";
    else if (/Chrome\//.test(ua)) browser = "Chrome";
    else if (/Firefox\//.test(ua)) browser = "Firefox";
    else if (/Safari\//.test(ua)) browser = "Safari";

    if (/iPhone|iPad/.test(ua)) os = "iOS";
    else if (/Android/.test(ua)) os = "Android";
    else if (/Windows/.test(ua)) os = "Windows";
    else if (/Macintosh|Mac OS X/.test(ua)) os = "macOS";
    else if (/Linux/.test(ua)) os = "Linux";

    return { browser, os };
}
